import apiClient from '../config/api';

/**
 * Check if the currently logged-in user is an Admin or Super Admin.
 * Admins and Super Admins have access to all plants.
 */
const isAdminUser = () => {
  try {
    const userStr = localStorage.getItem('user');
    if (!userStr) return false;
    const user = JSON.parse(userStr);
    const role = (user?.roleName || user?.RoleName || '').toLowerCase();
    return role === 'admin' || role === 'super admin';
  } catch {
    return false;
  }
};

/**
 * Get the plant IDs assigned to the current user from localStorage.
 * Returns an empty array if not set.
 */
const getAssignedPlantIds = () => {
  try {
    const raw = localStorage.getItem('plantIds');
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const plantService = {
  /**
   * Get plants for the current tenant.
   * - Admins / Super Admins: returns ALL tenant plants.
   * - Regular users: returns only the plants assigned to them.
   * @returns {Promise} Array of plants
   */
  getAllPlants: async () => {
    const response = await apiClient.get('/plant');
    const allPlants = response.data;

    // Admins and Super Admins can see every plant
    if (isAdminUser()) {
      return allPlants;
    }

    // For regular users, filter to only their assigned plants
    const assignedIds = getAssignedPlantIds();

    // If no plant IDs are assigned, return empty (not even a default fallback)
    if (!assignedIds || assignedIds.length === 0) {
      return [];
    }

    return allPlants.filter(plant => assignedIds.includes(plant.plantId));
  },

  /**
   * Get plant by ID
   * @param {number} plantId - Plant ID
   * @returns {Promise} Plant details
   */
  getPlantById: async (plantId) => {
    const response = await apiClient.get(`/plant/${plantId}`);
    return response.data;
  },

  /**
   * Create a new plant
   * @param {object} plantData - Plant data
   * @returns {Promise} Created plant
   */
  createPlant: async (plantData) => {
    const response = await apiClient.post('/plant', plantData);
    return response.data;
  },

  /**
   * Update plant
   * @param {number} plantId - Plant ID
   * @param {object} plantData - Updated plant data
   * @returns {Promise} Updated plant
   */
  updatePlant: async (plantId, plantData) => {
    const response = await apiClient.post(`/plant/update/${plantId}`, plantData);
    return response.data;
  },

  /**
   * Delete plant
   * @param {number} plantId - Plant ID
   * @returns {Promise} Deletion result
   */
  deletePlant: async (plantId) => {
    const response = await apiClient.post(`/plant/delete/${plantId}`);
    return response.data;
  },

  /**
   * Check if the current user is an admin/super admin
   * @returns {boolean}
   */
  isAdminUser,

  /**
   * Get assigned plant IDs from localStorage
   * @returns {number[]}
   */
  getAssignedPlantIds,
};

export default plantService;

